import { AxiosContext } from "contexts/AxiosContext";
import { AxiosInstance, AxiosResponse } from "axios";
import { useContext } from "react";
import {
  useQueries,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";
import { DataSource, BaseSeries, RawData } from "interfaces/chart";

export interface ScalarsHiInterface {
  date_time: string;
  machine__name: string;
  resolution: number;
  site__name: string;
  tag__name: string;
  values: number[];
}

export interface ScalarsHiRangeInterface {
  date_time: string;
  resolution: number;
  min: number[];
  max: number[];
}

interface ScalarsHiResponse {
  results: ScalarsHiInterface[];
}

interface ScalarsHiRangeResponse {
  results: ScalarsHiRangeInterface[];
}

const fetchScalarsHiData = async (
  queryKey: [string, number | null, number | null],
  axiosInstance: AxiosInstance
): Promise<RawData> => {
  const [_apiKey, machineId, tagId] = queryKey;

  let resp: AxiosResponse<ScalarsHiResponse> | null = null;

  if (machineId !== null && tagId !== null) {
    resp = await axiosInstance.get<ScalarsHiResponse>(
      `/scalars_hi?size=max&machine_id__in=${machineId}&tag_id__in=${tagId}`
    );
  }

  if (resp) {
    return mapScalarsDayToPlotData(resp.data.results);
  } else {
    return [];
  }
};

const fetchScalarsHiMedianData = async (
  queryKey: [string, number | null, number | null],
  axiosInstance: AxiosInstance
): Promise<RawData> => {
  const [_apiKey, siteId, tagId] = queryKey;
  let resp: AxiosResponse<ScalarsHiResponse> | null = null;

  if (siteId !== null && tagId !== null) {
    resp = await axiosInstance.get<ScalarsHiResponse>(
      `/scalars_hi/median?site=${siteId}&tag=${tagId}`
    );
  }

  if (resp) {
    return mapScalarsDayToPlotData(resp.data.results);
  } else {
    return [];
  }
};

const fetchScalarsHiRangeData = async (
  queryKey: [string, number | null, number | null],
  axiosInstance: AxiosInstance
): Promise<RawData> => {
  const [_apiKey, siteId, tagId] = queryKey;
  let resp: AxiosResponse<ScalarsHiRangeResponse> | null = null;

  if (siteId !== null && tagId !== null) {
    resp = await axiosInstance.get<ScalarsHiRangeResponse>(
      `/scalars_hi/values_range?site=${siteId}&tag=${tagId}`
    );
  }

  if (resp) {
    return mapScalarsRangeToPlotData(resp.data.results);
  } else {
    return [];
  }
};

export function mapScalarsDayToPlotData(datas: ScalarsHiInterface[]): RawData {
  let dates: Date[] = [];
  let values: number[] = [];
  for (const data of datas) {
    let date = new Date(data.date_time);
    let x: Date[] = [];
    for (let i: number = 0; i < data.values.length; i++) {
      // construct the new date in seconds since the start date
      let newDate = new Date(date.getTime() + data.resolution * i * 1000);
      x.push(newDate);
    }
    dates.push.apply(dates, x);
    values.push.apply(values, data.values);
  }

  let result: [number, number][] = [];
  for (let i = 0; i < dates.length; i++) {
    result.push([dates[i].getTime(), values[i]]);
  }

  return result;
}

export function mapScalarsRangeToPlotData(
  datas: ScalarsHiRangeInterface[]
): RawData {
  let dates: Date[] = [];
  let min: number[] = [];
  let max: number[] = [];
  for (const data of datas) {
    let date = new Date(data.date_time);
    let x: Date[] = [];
    for (let i: number = 0; i < data.min.length; i++) {
      // construct the new date in seconds since the start date
      let newDate = new Date(date.getTime() + data.resolution * i * 1000);
      x.push(newDate);
    }
    dates.push.apply(dates, x);
    min.push.apply(min, data.min);
    max.push.apply(max, data.max);
  }

  let result: [number, number, number][] = [];
  for (let i = 0; i < dates.length; i++) {
    result.push([dates[i].getTime(), min[i], max[i]]);
  }

  return result;
}

/**
 * This hook can be used to take a list of series that make up a chart, and fetch all of the necessary
 * data for displaying that chart by mapping the series such that their 'DataSource' objects are replaced
 * with RawDataSource(s).
 * @param series
 * @returns
 */
export function useGetData(
  series: BaseSeries<DataSource>[]
): UseQueryResult<RawData, Error>[] {
  const { axios, loading: axiosLoading } = useContext(AxiosContext)!;

  const queries: UseQueryOptions<
    RawData,
    Error,
    RawData,
    [string, number | null, number | null]
  >[] = series.map(({ dataSource: d }) => {
    switch (d.type) {
      case "scada-datetime-tag":
        return {
          queryKey: ["scalars_hi", d.args.machineId, d.args.tagId],
          queryFn: ({ queryKey }) => fetchScalarsHiData(queryKey, axios),
          enabled: !axiosLoading,
          staleTime: 10 * 60 * 1000,
          refetchInterval: 10 * 60 * 1000
        };
      case "scada-median":
        return {
          queryKey: ["scalars_hi/median", d.args.siteId, d.args.tagId],
          queryFn: ({ queryKey }) => fetchScalarsHiMedianData(queryKey, axios),
          enabled: !axiosLoading,
          staleTime: 10 * 60 * 1000,
          refetchInterval: 10 * 60 * 1000
        };
      case "scada-range":
        return {
          queryKey: ["scalars_hi/values_range", d.args.siteId, d.args.tagId],
          queryFn: ({ queryKey }) => fetchScalarsHiRangeData(queryKey, axios),
          enabled: !axiosLoading,
          staleTime: 10 * 60 * 1000,
          refetchInterval: 10 * 60 * 1000
        };
      default:
        throw new Error(`Unhandled data source type: ${d.type}`);
    }
  });

  return useQueries({ queries });
}
